// Vuex state object to encapsulate a user's claim

// @todo Seperate app meta data from claims data
// initial state
import {User} from '../../model/user'
import {Address} from '../../model/address'
import {Flight} from '../../model/flight'
import {BaggageItem} from '../../model/baggageItem'
import {ContentItem} from '../../model/contentItem'
import {DamageClaim} from '../../model/damageClaim'
import {Language} from '../../model/language'
import {SelectedValue} from '../../model/selectedValue'
import {AreaOfDamageImage} from '../../model/areaOfDamageImage'
import {ImageMeta} from '../../model/imageMeta'
import { MsUtils } from '@/utils/msutils'
import {Airport} from '@/model/airport'

const state = initState({})

// getters
const getters = {
  verificationCode: state => state.data.verificationCode,
  claimId: state => state.data.claimId,
  claimType: state => state.data.claimType,
  submittedClaimId: state => state.data.submittedClaimId,
  language: state => state.data.language,
  timeWhenScreensaver: state => state.data.timeWhenScreensaver,
  dataProtectionConsent: state => state.data.dataProtectionConsent,
  firstName: state => state.data.user.firstName,
  surname: state => state.data.user.surname,
  email: state => state.data.user.email,
  phone: state => state.data.user.phone,
  phone2: state => state.data.user.phone2,
  fiscalCode: state => state.data.user.fiscalCode,
  address: state => state.data.address,
  tempAddress: state => state.data.tempAddress,
  bookingReference: state => state.data.flight.bookingReference,
  flightReference: state => state.data.flight.flightReference,
  flightDate: state => state.data.flight.flightDate,
  flightData: state => state.data.flight.flightData,
  arrivalAirport: state => state.data.flight.arrivalAirport,
  departureAirport: state => state.data.flight.departureAirport,
  isReported: state => state.data.isReported,
  hasTempAddress: state => state.data.hasTempAddress,
  plannedTravelDate: state => state.data.plannedTravelDate,
  plannedTravel: state => state.data.plannedTravel,
  pirReportReference: state => state.data.pirReportReference,
  isPirPulledFromAirportReport: state => state.data.isPirPulledFromAirportReport,
  dateReported: state => state.data.dateReported,
  dateAtTempAddress: state => state.data.dateAtTempAddress,
  baggageItems: state => state.data.baggageItems,
  contentItems: state => state.data.contentItems,
  itemCount: state => {
    return state.data.baggageItems.length + state.data.contentItems.length
  },
  additionalInfo: state => state.data.additionalInfo,
  buildId: state => state.data.buildId,
  useCustomFlightData: state => state.meta.useCustomFlightData,
  termsAgreed: state => state.data.termsAgreed,
  infoConfirmed: state => state.data.infoConfirmed,
  pirSubmitted: state => state.meta.pirSubmitted,
  boardingPass: state => state.data.boardingPass,
  boardingPassConfirmed: state => state.meta.boardingPassConfirmed,
  isKiosk: state => state.meta.isKiosk,
  damageClaim: state => {
    // build up a DamageClaim object from our Vuex raw data

    // todo if kioks then set isReported to false
    // isReported == has been previously reported and kiosk users won't see this option
    if (state.meta.isKiosk) {
      state.data.isReported = false
    }

    // basic claim data
    let claim = {
      claimType: state.data.claimType,
      isPirPulledFromAirportReport: state.data.isPirPulledFromAirportReport,
      language: state.data.language,
      user: state.data.user,
      address: state.data.address,
      tempAddress: state.data.tempAddress,
      flight: state.data.flight,
      dataProtectionConsent: state.data.dataProtectionConsent,
      isReported: state.data.isReported,
      hasTempAddress: state.data.hasTempAddress,
      plannedTravelDate: state.data.plannedTravelDate,
      plannedTravel: state.data.plannedTravel,
      pirReportReference: state.data.pirReportReference,
      dateReported: state.data.dateReported,
      dateAtTempAddress: state.data.dateAtTempAddress,
      additionalInfo: state.data.additionalInfo,
      buildId: state.data.buildId,
      termsAgreed: state.data.termsAgreed,
      infoConfirmed: state.data.infoConfirmed,
      isKiosk: state.meta.isKiosk,
      boardingPassRaw: state.data.boardingPass ? state.data.boardingPass.raw : '',
      boardingPassConfirmed: state.meta.boardingPassConfirmed !== null ? state.meta.boardingPassConfirmed : false
    }
    // baggage items
    claim.baggageItems = convertToBaggageItems()
    // content items
    claim.contentItems = convertToContentItems()
    return new DamageClaim(claim)
  }
}

// actions
const actions = {
}

// mutations
const mutations = {
  setVerificationCode (state, verificationCode) {
    state.data.verificationCode = verificationCode
  },
  setClaimId (state, claimId) {
    state.data.claimId = claimId
  },
  setClaimType (state, claimType) {
    state.data.claimType = claimType
  },
  setIsPirPulledFromAirportReport (state, isPirPulledFromAirportReport) {
    state.data.isPirPulledFromAirportReport = isPirPulledFromAirportReport
  },
  setSubmittedClaimId (state, value) {
    state.data.submittedClaimId = value
  },
  setLanguage (state, language) {
    state.data.language = new Language(language)
  },
  /**
   * Set datetime object when to expect screensaver to appear
   * @param {Object} state state object
   * @param {Number} timeWhenScreensaver date time when screensaver appears
   */
  setTimeWhenScreensaver (state, timeWhenScreensaver) {
    state.data.timeWhenScreensaver = timeWhenScreensaver
  },
  setDataProtectionConsent (state, consent) {
    state.data.dataProtectionConsent = consent
  },
  setFirstName (state, firstName) {
    state.data.user.firstName = firstName
  },
  setSurname (state, surname) {
    state.data.user.surname = surname
  },
  setEmail (state, email) {
    state.data.user.email = email
  },
  setPhone (state, phone) {
    state.data.user.phone = phone
  },
  setPhone2 (state, phone2) {
    state.data.user.phone2 = phone2
  },
  setFiscalCode (state, fiscalCode) {
    state.data.user.fiscalCode = fiscalCode
  },
  setAddress (state, address) {
    state.data.address = address
  },
  setTempAddress (state, address) {
    state.data.tempAddress = address
  },
  setBookingReference (state, reference) {
    state.data.flight.bookingReference = reference
  },
  setFlightReference (state, reference) {
    state.data.flight.flightReference = reference
  },
  setFlightDate (state, date) {
    state.data.flight.flightDate = date
  },
  setFlightData (state, data) {
    state.data.flight.flightData = data
  },
  setArrivalAirport (state, data) {
    state.data.flight.arrivalAirport = data
  },
  setDepartureAirport (state, data) {
    state.data.flight.departureAirport = data
  },
  setIsReported (state, isReported) {
    state.data.isReported = isReported
  },
  setHasTempAddress (state, hasTempAddress) {
    state.data.hasTempAddress = hasTempAddress
  },
  setPlannedTravel (state, plannedTravel) {
    state.data.plannedTravel = plannedTravel
  },
  setPlannedTravelDate (state, date) {
    state.data.plannedTravelDate = date
  },
  setPirReportReference (state, reference) {
    state.data.pirReportReference = reference
  },
  setDateReported (state, date) {
    state.data.dateReported = date
  },
  setDateAtTempAddress (state, date) {
    state.data.dateAtTempAddress = date
  },
  addBaggageItem (state, item) {
    state.data.baggageItems.push(item)
  },
  replaceBaggageItem (state, item) {
    state.data.baggageItems[item.index] = item.data
  },
  deleteBaggageItem (state, index) {
    state.data.baggageItems.splice(index, 1)
  },
  addContentsItem (state, item) {
    state.data.contentItems.push(item)
  },
  replaceContentsItem (state, item) {
    state.data.contentItems[item.index] = item.data
  },
  deleteContentsItem (state, index) {
    state.data.contentItems.splice(index, 1)
  },
  setAdditionalInfo (state, info) {
    state.data.additionalInfo = info
  },
  setBuildId (state, buildId) {
    state.data.buildId = buildId
  },
  setUseCustomFlightData (state, useCustomFlightData) {
    state.meta.useCustomFlightData = useCustomFlightData
  },
  setTermsAgreed (state, value) {
    state.data.termsAgreed = value
  },
  setInfoConfirmed (state, value) {
    state.data.infoConfirmed = value
  },
  setBaggageReplacementOption (state, value) {
    if (value.replacementItem) {
      state.data.baggageItems[value.baggageItem].replacementVariantId = value.replacementItem.id
      state.data.baggageItems[value.baggageItem].replacementColorId = value.colorId
      state.data.baggageItems[value.baggageItem].replacementAltColorIds = value.altColorIds
    } else {
      state.data.baggageItems[value.baggageItem].replacementVariantId = null
      state.data.baggageItems[value.baggageItem].replacementColorId = null
      state.data.baggageItems[value.baggageItem].replacementAltColorIds = null
    }
    state.data.baggageItems[value.baggageItem].replacementChosen = true
  },
  setPirSubmitted (state, submitted) {
    state.meta.pirSubmitted = submitted
  },
  resetState (state) {
    initState(state)
  },
  setBoardingPass (state, boardingPass) {
    state.data.boardingPass = boardingPass
  },
  setBoardingPassConfirmed (state, confirmed) {
    if (confirmed) {
      confirmBoardingPass(state)
    } else {
      resetBoardingPass(state)
    }
  },
  // clear boarding pass and
  // everything copied from boarding pass!
  clearBoardingPassData (state) {
    clearBoardingPass(state)
  },
  setIsKiosk (state, isKiosk) {
    state.meta.isKiosk = isKiosk
  }
}

function initState (state) {
  state.data = {
    verificationCode: null,
    claimId: MsUtils.uuidv4(),
    claimType: null,
    isPirPulledFromAirportReport: false,
    submittedClaimId: null,
    language: new Language({}),
    timeWhenScreensaver: null,
    dateReported: null,
    dateAtTempAddress: null,
    user: new User({}),
    address: new Address({}),
    tempAddress: new Address({}),
    flight: new Flight({}),
    dataProtectionConsent: false,
    isReported: true,
    hasTempAddress: false,
    plannedTravel: false,
    plannedTravelDate: null,
    pirReportReference: '',
    contentItems: [],
    baggageItems: [],
    additionalInfo: '',
    buildId: '',
    termsAgreed: false,
    infoConfirmed: false,
    boardingPass: null
  }
  state.meta = {
    useCustomFlightData: false,
    pirSubmitted: false,
    boardingPassConfirmed: false
  }
  return state
}

function convertToBaggageItems () {
  let baggageItems = []
  state.data.baggageItems.forEach(item => {
    baggageItems.push(BaggageItem.BaggageItemFromFlat({...item}))
  })
  return baggageItems
}

function convertToContentItems () {
  let contentItems = []
  state.data.contentItems.forEach(item => {
    let options = {
      purchaseDate: item.purchaseDate,
      currency: new SelectedValue(item.currency, item.currencyString),
      price: item.price,
      name: item.name,
      damageDesc: item.damageDesc
    }
    // fixed images
    if (item.images.wholeItem && item.images.wholeItem.imageUrl) {
      options.wholeItemImage = new ImageMeta({
        imageUrl: item.images.wholeItem.imageUrl
      })
    }
    if (item.receipt && item.receipt.imageUrl) {
      options.receipt = new ImageMeta({
        imageUrl: item.receipt.imageUrl
      })
    }
    // area of damage images
    options.areaOfDamageImages = convertToAreaOfDamageImages(item.areaOfDamageImages)
    // todo receipts
    contentItems.push(new ContentItem(options))
  })
  return contentItems
}

function convertToAreaOfDamageImages (areaOfDamageImages) {
  let images = []
  areaOfDamageImages.forEach(image => {
    if (image.imageUrl) {
      images.push(new AreaOfDamageImage({
        imageUrl: image.imageUrl,
        selectVal: image.selectVal
      }))
    }
  })
  return images
}

function resetBoardingPass (state) {
  console.log('reset boarding pass')
  state.meta.boardingPassConfirmed = false
  state.data.boardingPass = null
}

function confirmBoardingPass (state) {
  console.log('confirm boarding pass')
  state.meta.boardingPassConfirmed = true
  // copy data from boarding pass to flight and user object
  state.data.user.firstName = state.data.boardingPass.firstName
  state.data.user.surname = state.data.boardingPass.lastName
  state.data.flight.bookingReference = state.data.boardingPass.bookingReference
  state.data.flight.flightReference = state.data.boardingPass.flightNumber
  state.data.flight.arrivalAirport = new Airport({ code: state.data.boardingPass.arrival })
  state.data.flight.departureAirport = new Airport({ code: state.data.boardingPass.departure })
  state.data.flight.flightDate = state.data.boardingPass.date
}
// removes all data potentially copied form boarding pass
function clearBoardingPass (state) {
  console.log('clear boarding pass')
  resetBoardingPass(state)

  // reset data from boarding pass
  state.data.user.firstName = ''
  state.data.user.surname = ''
  state.data.flight.bookingReference = ''
  state.data.flight.flightReference = ''
  state.data.flight.arrivalAirport = null
  state.data.flight.departureAirport = null
  state.data.flight.flightDate = ''
}

export default {
  state,
  getters,
  actions,
  mutations
}
